<script setup>
import {
  WButton,
  WCheckbox,
  WHelper,
  WLabel,
  WParagraph,
  WSelect,
  WSwitch,
  WTextfield
} from '@alice-health/wonderland-vue'
import { computed, inject, reactive, watch } from 'vue'
import JsonViewer from 'vue-json-viewer'

import { HTextfield } from '@/components/shared/atoms'
import {
  CHANNEL_CATEGORIES_LABEL,
  CHANNEL_KINDS_LABEL,
  CHANNEL_SUBCATEGORIES_LABEL,
  CHANNEL_SUBCATEGORY_CLASSIFIERS_LABEL,
  ChannelStatus,
  ChannelStatusMap,
  MessageTypes
} from '@/models/channels'
import { deleteField } from '@/modules/auth/index-firebase'
import DatabaseFactory from '@/wrappers/database'

import CDebugChannelMessage from './CDebugChannelMessage'
import { transformDates } from './DebugUtils'

const confirm = inject('confirm')
const notify = inject('notify')

const getOptionsValues = options => {
  return Object.entries(options).map(([id, value]) => ({
    id,
    value
  }))
}

const state = reactive({
  filters: [
    'name',
    'kind',
    'category',
    'subCategory',
    'subCategoryClassifier',
    'personId',
    'status'
  ],
  id: '',
  name: '',
  personId: '',
  kind: '',
  type: '',
  category: '',
  subCategory: '',
  subCategoryClassifier: '',
  status: '',
  kindOptions: getOptionsValues(CHANNEL_KINDS_LABEL),
  categoryOptions: getOptionsValues(CHANNEL_CATEGORIES_LABEL),
  subCategoryOptions: getOptionsValues(CHANNEL_SUBCATEGORIES_LABEL),
  subCategoryClassifierOptions: getOptionsValues(
    CHANNEL_SUBCATEGORY_CLASSIFIERS_LABEL
  ),
  statusOptions: Object.entries(ChannelStatusMap).map(([id, value]) => ({
    id,
    value
  })),
  showStaffMessages: false,
  showMemberMessages: false,
  showPrivateMessages: false,
  messageLimit: 20,
  channelLimit: 20,
  channels: null,
  memberName: 'Membro',
  showMergedChannels: false,
  messageTypes: Object.values(MessageTypes),
  selectedMessageType: []
})
const disableSearch = computed(() => {
  return (
    state.id === '' &&
    state.name === '' &&
    state.type === '' &&
    state.status === '' &&
    state.personId === ''
  )
})
const currentChannel = computed(() => {
  const channels = state.channels && Object.values(state.channels)
  if (channels.length === 1) {
    return channels[0]
  }

  return undefined
})
const showActiveButton = computed(() => {
  return (
    currentChannel.value &&
    (currentChannel.value.status !== ChannelStatus.ACTIVE ||
      currentChannel.value.archivedAt)
  )
})
const showPublicTypingButton = computed(() => {
  return currentChannel.value?.typing?.length
})
const showPrivateTypingButton = computed(() => {
  return currentChannel.value?.privateTyping?.length
})
const showMessageActions = computed(() => {
  return (
    state.id &&
    Object.values(state.channels).length === 1 &&
    (state.showStaffMessages ||
      state.showMemberMessages ||
      state.showPrivateMessages)
  )
})
const channelCollection = computed(() => {
  return state.showMergedChannels ? 'channels_merged' : 'channels'
})
watch(
  () => state.showStaffMessages,
  value => {
    if (value) {
      state.showPrivateMessages = false
    }
  }
)

watch(
  () => state.showMemberMessages,
  value => {
    if (value) {
      state.showPrivateMessages = false
    }
  }
)
watch(
  () => state.showPrivateMessages,
  value => {
    if (value) {
      state.showStaffMessages = false
      state.showMemberMessages = false
    }
  }
)
const search = () => {
  if (state.id) {
    getOne()
    return
  }
  getAll()
}
const getOne = async () => {
  const database = new DatabaseFactory(
    `${channelCollection.value}/${state.id}`
  ).getReadDatabase()

  const snapshot = await database.getOne()
  state.channels = snapshot.data() ? await parseChannels([snapshot]) : []
}
const getAll = async () => {
  const database = new DatabaseFactory(
    channelCollection.value
  ).getReadDatabase()

  const query = mountFilters()
  query.limit = Number(state.channelLimit)

  const snapshot = await database.get(query)

  state.channels = await parseChannels(snapshot.docs)
}
const mountFilters = () => {
  const query = {
    filters: []
  }

  for (const filter of state.filters) {
    if (state[filter]) {
      query.filters.push({
        field: filter,
        condition: '==',
        value: state[filter]
      })
    }
  }

  return query
}
const parseChannels = async channels => {
  let parsedChannels = channels
    .map(snap => {
      const channel = snap.data()
      const channelName = channel?.name || '{canal sem nome}'

      return {
        ...transformDates(channel),
        channelId: snap.id,
        name: channelName
      }
    })
    .sort((a, b) => {
      if (a.createdAt > b.createdAt) return -1
      else if (b.createdAt > a.createdAt) return 1
      return 0
    })

  if (
    state.showStaffMessages ||
    state.showMemberMessages ||
    state.showPrivateMessages
  ) {
    parsedChannels = await fillMessages(parsedChannels)
  }

  parsedChannels.forEach(channel => {
    delete channel.personId
    delete channel.channelPersonId

    if (channel.lastPreviewableMessage) {
      deleteFieldsFromMessage(channel.lastPreviewableMessage)
    }
  })

  if (
    state.showStaffMessages ||
    state.showMemberMessages ||
    state.showPrivateMessages
  ) {
    parsedChannels = await fillMessages(parsedChannels)
  }

  parsedChannels.forEach(channel => {
    delete channel.personId
    delete channel.channelPersonId

    if (channel.lastPreviewableMessage) {
      channel.lastPreviewableMessage = parseMessage(
        channel.lastPreviewableMessage,
        channel
      )
    }
  })

  return parsedChannels.reduce((map, channel) => {
    const id = channel.channelId
    map[id] = channel
    return map
  }, {})
}
const fillMessages = async channels => {
  const messageCollection = state.showPrivateMessages
    ? 'privateMessages'
    : 'messages'
  const query = {
    filters: [],
    orderBy: [{ field: 'createdAt', sort: 'desc' }],
    limit: Number(state.messageLimit)
  }
  if (state.selectedMessageType.length > 0) {
    query.filters.push({
      field: 'type',
      condition: 'in',
      value: state.selectedMessageType
    })
  }
  return await Promise.all(
    channels.map(async channel => {
      const database = new DatabaseFactory(
        `${channelCollection.value}/${channel.channelId}/${messageCollection}`
      ).getReadDatabase()

      const messagesSnap = await database.get(query)

      channel[messageCollection] = messagesSnap.docs
        .map(messageDoc => {
          const message = messageDoc.data()
          return parseMessage(message, channel, messageDoc.id)
        })
        .filter(
          message =>
            state.showPrivateMessages ||
            (state.showMemberMessages && message.from === state.memberName) ||
            (state.showStaffMessages && message.from !== state.memberName)
        )

      return channel
    })
  )
}
const parseMessage = (rawMessage, channel, messageId) => {
  const message = { ...rawMessage }
  const isFromMember = message.userId === channel.channelPersonId
  const staff = Object.values(channel.staff).find(
    staff => staff.id === message.userId
  )
  const staffHistory = channel.staffHistory
    ? Object.values(channel.staffHistory).find(
        staff => staff.id === message.userId
      )
    : null

  let from = 'Desconhecido'

  if (isFromMember) from = state.memberName
  else if (message.userId === '') from = 'Backend'
  else from = staff?.name || staffHistory?.name || 'Desconhecido'

  deleteFieldsFromMessage(message)

  return {
    ...transformDates(message),
    messageId: messageId,
    from
  }
}
const deleteFieldsFromMessage = message => {
  delete message.aliceId
  delete message.userId
  delete message.content
  delete message.deletedContent
}
const getChannel = async () => {
  const database = new DatabaseFactory(
    `channels/${currentChannel.value.channelId}`
  ).getReadDatabase()
  return await database.getOne()
}
const updateMemberInput = async () => {
  const confirmed = await confirm({
    title: `Tem certeza que deseja ${
      currentChannel.value.hideMemberInput ? 'exibir' : 'esconder'
    } o input do membro?`
  })
  if (confirmed) {
    updateChannel({ hideMemberInput: !currentChannel.value.hideMemberInput })
  }
}
const activeChannel = async () => {
  const confirmed = await confirm({
    title: 'Tem certeza que deseja reativar o canal?',
    description:
      'Isso mudará o status para "Ativo" e apagará a mensagem de "Conversa Encerrada"'
  })

  if (confirmed) {
    const channel = await getChannel()

    if (
      channel.data().status !== ChannelStatus.ARCHIVED &&
      !channel.data().archivedAt
    ) {
      resetData()
      return
    }
    await removeArchivedMessage()

    updateChannel(
      {
        status: ChannelStatus.ACTIVE,
        csatUrl: deleteField(),
        archivedAt: null
      },
      'Canal ativado com sucesso',
      'Erro ao ativar canal'
    )
  }
}
const removeArchivedMessage = async () => {
  const database = new DatabaseFactory(
    `channels/${currentChannel.value.channelId}/messages`
  ).getReadDatabase()

  const query = {
    limit: 1,
    orderBy: [{ field: 'createdAt', sort: 'desc' }]
  }

  const messages = await database.get(query)

  const lastMessageDoc = messages.docs[0]
  if (
    lastMessageDoc &&
    lastMessageDoc.data().type === MessageTypes.CHANNEL_ARCHIVED
  ) {
    await lastMessageDoc.ref.delete()
  }
}
const updateChannel = async (data, messageSuccess, messageError) => {
  const database = new DatabaseFactory(
    `channels/${currentChannel.value.channelId}`
  ).getWriteDatabase()
  try {
    await database.update(data)

    resetData()

    notify({
      description: messageSuccess || 'Canal atualizado com sucesso!',
      duration: 6000
    })
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e)

    notify({
      description: messageError || 'Ocorreu um erro ao atualizar o canal',
      duration: 6000
    })
  }
}
const resetTyping = async typingProperty => {
  const typingMap = {
    typing: 'público',
    privateTyping: 'privado'
  }

  const totalTyping = currentChannel.value[typingProperty].length

  const confirmed = await confirm({
    title: `Tem certeza que deseja limpar o "digitando" no ${typingMap[typingProperty]}?`,
    description: `Vamos limpar ${totalTyping} pessoa${
      totalTyping === 1 ? '' : 's'
    } digitando nesse canal`
  })

  if (confirmed) {
    const channel = await getChannel()

    if (!channel.data()[typingProperty].length) {
      resetData()
      return
    }

    updateChannel(
      { [typingProperty]: [] },
      `"Digitando" ${typingMap[typingProperty]} zerado com sucesso!`,
      'Ocorreu um erro ao limpar o "digitando"'
    )
  }
}
const resetData = () => {
  state.id = ''
  state.name = ''
  state.type = ''
  state.status = ''
  state.showStaffMessages = false
  state.showMemberMessages = false
  state.showPrivateMessages = false
  state.messageLimit = 20
  state.channelLimit = 20
  state.channels = null
}
</script>

<template>
  <section class="debug__grid">
    <form class="debug__form" @submit.prevent>
      <WTextfield v-model="state.id" label="Id" class="debug__input" />

      <WCheckbox
        v-model="state.showMergedChannels"
        class="debug__input"
        label="Buscar em canais mergeados"
      />

      <WTextfield
        v-model="state.name"
        label="Nome do canal"
        :disabled="!!state.id"
        class="debug__input"
      />

      <WTextfield
        v-model="state.personId"
        label="Person Id - final 01"
        :disabled="!!state.id"
        class="debug__input"
      />

      <div class="debug__input">
        <WSelect v-model="state.kind" label="Tipo" :disabled="!!state.id">
          <option value="">Selecione um tipo</option>
          <option
            v-for="kindOption in state.kindOptions"
            :key="kindOption.id"
            :value="kindOption.id"
          >
            {{ kindOption.value }}
          </option>
        </WSelect>
      </div>
      <div class="debug__input">
        <WSelect
          v-model="state.category"
          label="Categoria"
          :disabled="!!state.id"
        >
          <option value="">Selecione uma categoria</option>
          <option
            v-for="categoryOption in state.categoryOptions"
            :key="categoryOption.id"
            :value="categoryOption.id"
          >
            {{ categoryOption.value }}
          </option>
        </WSelect>
      </div>
      <div class="debug__input">
        <WSelect
          v-model="state.subCategory"
          label="Subcategoria"
          :disabled="!!state.id"
        >
          <option value="">Selecione uma subcategoria</option>
          <option
            v-for="subCategoryOption in state.subCategoryOptions"
            :key="subCategoryOption.id"
            :value="subCategoryOption.id"
          >
            {{ subCategoryOption.value }}
          </option>
        </WSelect>
      </div>
      <div class="debug__input">
        <WSelect
          v-model="state.subCategoryClassifier"
          label="Classificador da subcategoria"
          :disabled="!!state.id"
        >
          <option value="">Selecione classificador</option>
          <option
            v-for="subCategoryClassifierOption in state.subCategoryClassifierOptions"
            :key="subCategoryClassifierOption.id"
            :value="subCategoryClassifierOption.id"
          >
            {{ subCategoryClassifierOption.value }}
          </option>
        </WSelect>
      </div>

      <div class="debug__input">
        <WSelect v-model="state.status" label="Status" :disabled="!!state.id">
          <option value="">Selecione um status</option>
          <option
            v-for="statusOption in state.statusOptions"
            :key="statusOption.id"
            :value="statusOption.id"
          >
            {{ statusOption.value }}
          </option>
        </WSelect>
      </div>

      <WCheckbox
        v-model="state.showStaffMessages"
        class="debug__input"
        label="Exibir mensagem do Time de Saúde"
      />

      <WCheckbox
        v-model="state.showMemberMessages"
        class="debug__input"
        label="Exibir mensagem de membros"
      />

      <WCheckbox
        v-model="state.showPrivateMessages"
        class="debug__input"
        label="Exibir mensagem em background"
      />

      <div class="debug__input">
        <WParagraph>
          Tipo de mensagem (⇧ + Click para selecionar multiplos, ⌘ + click para
          desselecionar 1, nenhum items selecionado buscará todos os tipos)
        </WParagraph>
        <WSelect
          v-model="state.selectedMessageType"
          class="debug__input"
          multiple
          :disabled="
            !state.showMemberMessages &&
            !state.showStaffMessages &&
            !state.showPrivateMessages
          "
        >
          <option
            v-for="messageType in state.messageTypes"
            :key="messageType"
            :value="messageType"
          >
            {{ messageType }}
          </option>
        </WSelect>
      </div>

      <div class="debug__input">
        <WLabel class="input">Últimas mensagens: </WLabel>
        <HTextfield
          v-model:value="state.messageLimit"
          class="input-number"
          type="number"
          :disabled="
            !state.showMemberMessages &&
            !state.showStaffMessages &&
            !state.showPrivateMessages
          "
        />
      </div>

      <div class="debug__input">
        <WLabel class="input">Limite de canais:</WLabel>
        <HTextfield
          v-model:value="state.channelLimit"
          class="input-number"
          type="number"
          :disabled="!!state.id"
        />
      </div>

      <WButton
        block
        :disabled="disableSearch"
        class="debug__cta"
        @click="search"
      >
        Buscar {{ state.id ? 'canal' : 'canais' }}
      </WButton>

      <WButton
        block
        type="button"
        variant="tertiary"
        class="debug__cta"
        @click="resetData"
      >
        Limpar filtros
      </WButton>
    </form>

    <aside v-if="state.channels" class="debug__results">
      <strong>Total de canais encontrados: {{ state.channels.length }}</strong>

      <article class="debug__info">
        <WHelper>
          Os canais e as mensagens são ordenados por
          <code>createdAt DESC</code>
        </WHelper>
        <br />
        <WHelper>O padrão das datas é MM/DD/YYYY, HH:MM:SS AM/PM</WHelper>
      </article>

      <JsonViewer
        class="debug__json"
        :value="state.channels"
        :expand-depth="2"
      />

      <CDebugChannelMessage v-if="showMessageActions" :channel-id="state.id" />

      <WButton
        v-if="showActiveButton"
        block
        contextual="primary"
        class="debug__cta"
        @click="activeChannel"
      >
        Ativar canal
      </WButton>

      <WButton
        v-if="showPublicTypingButton"
        block
        contextual="primary"
        class="debug__cta"
        @click="resetTyping('typing')"
      >
        Limpar quem está digitando no público
      </WButton>

      <WButton
        v-if="showPrivateTypingButton"
        block
        contextual="primary"
        class="debug__cta"
        @click="resetTyping('privateTyping')"
      >
        Limpar quem está digitando no privado
      </WButton>
      <div class="debug__cta">
        <WSwitch
          v-if="currentChannel"
          :checked="currentChannel.hideMemberInput"
          label="Input do membro escondido"
          @w-change="updateMemberInput"
        />
      </div>
    </aside>
  </section>
</template>

<style lang="scss">
.debug {
  &__grid {
    display: flex;
    gap: var(--gl-spacing-04);
    align-items: flex-start;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: var(--gl-spacing-01);
    width: 50%;
  }

  &__info {
    color: var(--sys-color-content-tertiary);
    font-size: var(--gl-font-size-xxs);
    margin: var(--gl-spacing-02) 0;
  }

  &__results {
    width: 50%;
  }

  &__results &__json {
    background: var(--sys-color-surface-background-default);
    margin-top: var(--gl-spacing-02);
    height: 562px;
    overflow: auto;
  }

  &__cta {
    width: 100%;
  }

  &__input {
    margin-bottom: var(--gl-spacing-04);
  }
  .toggle__label {
    font-size: var(--gl-font-size-sm);
    margin-left: var(--gl-spacing-04);
  }
}
</style>
